import { SoundByte } from "../../enum/sounds";
import { Time } from "../../lib/Time";
import Vector2 from "../../math/Vector2";
import { lerp } from "../../util";
import Color from "../../util/Color";
import { Random } from "../../util/Random";
import DialogueManager from "../DialogueManager";
import SPBullet from "../SPBullet";
import SPGameManager from "../SPGameManager";
import SPGun from "../SPGun";
import SPPlayer from "../SPPlayer";
import SmartBogey from "./SmartBogey";

export enum WolfState {
    InitialMeeting,
    FriendlyBattle,
    Deathmatch,
    Ally
}

enum DialogueState {
    MovingToPlayer,
    Intro,
    Leaving,
    Idle
}

export default class Wolf extends SmartBogey {
    xpReward: number = 100;
    color: Color = new Color(26,237,170);
    fireRate: number = 4;
    name: string = "Codename: W0LF";
    gun: SPGun = new SPGun(this, {
        bulletSpeed: 200,
        damage: 3,
        fireRate: 3,
        magSize: 30,
        mouseTrigger: 0,
        reloadTime: 1,
        maxEnemiesHit: 1,
    });

    wolfState: WolfState = WolfState.InitialMeeting;
    dialogueState: DialogueState = DialogueState.MovingToPlayer;
    targetPosition: Vector2 = Vector2.zero;

    Start(): void {
        super.Start();
        this.targetPosition = SPPlayer.Singleton.transform.position.add(Vector2.right.scale(128));

        this.gun.onShoot = () => {
            const center = this.transform.position.add(this.transform.forward.scale(this.size + 3));
                
            this.engine.NewGameObjectWithScript(SPBullet, {
                position: center.add(this.transform.left.scale(4)),
                initialValues: {
                    damage: this.gun.damage,
                    velocity: (this.isFriendly ? this.velocity : Vector2.zero).add(this.transform.forward.scale(this.gun.bulletSpeed)),
                    isPlayerBullet: this.isFriendly,
                    owner: this
                }
            });
            this.engine.NewGameObjectWithScript(SPBullet, {
                position: center.add(this.transform.right.scale(4)),
                initialValues: {
                    damage: this.gun.damage,
                    velocity: (this.isFriendly ? this.velocity : Vector2.zero).add(this.transform.forward.scale(this.gun.bulletSpeed)),
                    isPlayerBullet: this.isFriendly,
                    owner: this
                }
            });
        }
    }

    StartDialogue() {
        const dialogue = DialogueManager.Singleton;

        const accepted = () => {
            dialogue.Queue("Good. A wise decision.");
            dialogue.Queue("Prove yourself one more time. We have an outpost under attack—help me eliminate the aggressors, and you'll have earned your stay.");
        }

        const declined = () => {
            dialogue.Queue("A pity. I had hoped you were smarter.");
            dialogue.Queue("If you’re not with us… you’re against us.");
            this.TurnAggressive();
        }

        dialogue.Open("I am Commander WOLF of the Star Force. We watch over this sector, looking for warriors like you. Fighters with strength, skill... potential.", {name: "Wolf"});
        dialogue.Queue("You've proven yourself. Now, let's talk about your future.");
        dialogue.Queue("The Star Force has a place for you. Join us, and your strength will serve a greater purpose.", {
            choices: [
                {
                    text: "What exactly is this 'greater purpose?'",
                    callback: () => {
                        dialogue.Queue("Order. Power. Survival. The galaxy is lawless, full of factions tearing at each other like rabid beasts. The weak get crushed underfoot. But we—Star Force—stand above it all.");
                        dialogue.Queue("We bring control. We bring unity. Those who resist us think they fight for freedom, but they're just clinging to chaos.");
                        dialogue.Queue("We offer you the chance to be more than a lone warrior. With us, your strength will shape the future.", {
                            choices: [
                                {
                                    text: "You make it sound noble, but you just want power.",
                                    callback: () => {
                                        dialogue.Queue("Power is necessary. Do you think victory is won through sentiment?");
                                        dialogue.Queue("You're sharp enough to see through words, but are you wise enough to know when to seize opportunity? Join us, or die", {
                                            choices: [
                                                {
                                                    text: "Ok pal",
                                                    callback: () => {
                                                        accepted();
                                                    }
                                                },
                                                {
                                                    text: "No u",
                                                    callback: () => {
                                                        declined();
                                                    }
                                                },
                                            ]
                                        });
                                    }
                                },
                                {
                                    text: "If you're so strong, why do you need me?",
                                    callback: () => {
                                        dialogue.Queue("Heh, A fair question. We don't 'need' you. But you're a rare find—a fighter who survives against overwhelming odds. Talent like that deserves a place among us.");
                                        dialogue.Queue("We elevate the strong. The weak are… repurposed.", {
                                            choices: [
                                                {
                                                    text: "Ok buddy chill sure",
                                                    callback: () => {
                                                        accepted();
                                                    }
                                                },
                                                {
                                                    text: "Is that a threat?",
                                                    callback: () => {
                                                        declined();
                                                    }
                                                },
                                            ]
                                        });
                                    }
                                },
                                {
                                    text: "I see the appeal. What do I have to do to join?",
                                    callback: () => {
                                        accepted();
                                    }
                                },
                                {
                                    text: "I'm not interested. Find someone else.",
                                    callback: () => {
                                        declined();
                                    }
                                },
                            ]
                        });
                    }
                },
                {
                    text: "I fight for myself, not for some faction.",
                    callback: () => {
                        dialogue.Queue("Short-sighted, but expected. Lone wolves don't last long out here. Still, I admire your spirit.");
                        this.TurnAggressive();
                    }
                },
                {
                    text: "And if I refuse?",
                    callback: () => {
                        dialogue.Queue('Then you are a threat. And we do not tolerate threats.');
                        this.TurnAggressive();
                    }
                },
            ]
        });

        // dialogue.Queue("Order. Power. Survival. This galaxy is chaotic, but the strong can bring balance.");
        // dialogue.Queue("With us, you will be more than a lone warrior—you will be part of something unstoppable.");
        // dialogue.Queue("We—Star Force—stand above it all. We bring control. We bring unity.");
        // dialogue.Queue("Those who resist us think they fight for freedom, but they're just clinging to chaos.");
        // dialogue.Queue("We offer you the chance to be more than a lone warrior. With us, your strength will shape the future.");
        // dialogue.Queue("Think about it... For now, there are more bogeys coming.");
        // dialogue.Queue("I'll help you take them out.");
        dialogue.listener.once("onClose", () => {
            if (this.wolfState !== WolfState.InitialMeeting) return;
            this.isFriendly = true;
            this.dialogueState = DialogueState.Idle;
            this.wolfState = WolfState.Ally;
        });
    }

    MeetingUpdate() {
        switch (this.dialogueState) {
            case DialogueState.MovingToPlayer:
                this.velocity = this.targetPosition.sub(this.transform.position).normalized.scale(64);
                this.transform.rotation = this.velocity.normalized;

                if (Vector2.distance(this.transform.position, this.targetPosition) <= this.size) {
                    this.velocity = Vector2.zero;
                    this.dialogueState = DialogueState.Intro;
                    this.StartDialogue();
                }
                break;

            case DialogueState.Intro:
                let targetRot = SPPlayer.Singleton.transform.position.sub(this.transform.position).angle;
                let myRot = this.transform.rotation.angle;

                if (targetRot < 0) targetRot += 2 * Math.PI;
                if (myRot < 0) myRot += 2 * Math.PI;

                const lerpRot = lerp(myRot, targetRot, Time.deltaTime * Math.PI);
                this.transform.rotation = Vector2.fromRadians(lerpRot);
                break;
            case DialogueState.Leaving:
                this.acceleration = Vector2.up.scale(this.accelerationForce);
                this.transform.rotation = this.velocity.normalized;

                if (Vector2.distance(this.transform.position, SPPlayer.Singleton.transform.position) >= 600) {
                    this.engine.RemoveGameObject(this.gameObject);
                }
                break;
        
            default:
                break;
        }

        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(this.velocity.scale(this.friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));
    }

    Die(crashedIntoPlayer?: boolean): void {
        super.Die(crashedIntoPlayer);

        SPGameManager.Singleton.bWolfKilledEarly = true;
        SPPlayer.Singleton.cameraLocked = true;
    }

    TurnAggressive() {
        this.wolfState = WolfState.Deathmatch;
        this.isBoss = true;
        // SPPlayer.Singleton.cameraLocked = false;
        SPGameManager.Singleton.ChangeBGM(SoundByte.WolfBattle);
    }

    Damage(amt: number): void {
        if (this.wolfState === WolfState.InitialMeeting) {
            this.TurnAggressive();
            DialogueManager.Singleton.ClearQueue();
            DialogueManager.Singleton.Open("Ah... Impatient. Predictable. I was hoping you'd be smarter than that.");
            DialogueManager.Singleton.Queue("Very well. Consider this your final test.");
            return;
        }

        super.Damage(amt);
    }

    Update(): void {
        if (this.wolfState === WolfState.InitialMeeting) {
            this.MeetingUpdate();
        } else {
            super.Update();
        }
    }
}